import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Data from "../../../apiData/blog.json";
import { BlogArticleMeta } from "./BlogSeoMeta";
import useBlogAnalytics from "./useBlogAnalytics";
import LotusArt from "./BlogNoImage";
import {
  ArticlePage, BgBlob, DotGrid,
  ArticleHero, ArticleHeroText,
  ArticleInner, ArticleMeta, AuthorAvatar, MetaText, BackBtn,
  CategoryPill,
  ContentHeading, ContentParagraph, ContentImage,
  VideoWrap, ContentList, TableBlock, Blockquote, ContentLink,
  SeoSection, SeoLabel, SeoTags, SeoTag,
  EmptyState, NoImageArt,
  ArticleHeroPlain, ArticleHeroPlainText, ArticleHeroPlainArt,
} from "./Blog.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const ROOT_PATH = process.env.PUBLIC_URL + "/assets/ImagePackages";
const YT_EMBED  = "https://www.youtube.com/embed";
const ytThumb   = (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;

const heroImgSrc = (post) => {
  if (post.thumbnail?.startsWith("https://")) return post.thumbnail;
  if (post.asnVideoPath) return ytThumb(post.asnVideoPath);
  if (post.thumbnail) return `${ROOT_PATH}/${post.thumbnail}`;
  return null;
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const BackArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// ─── RenderBlock ──────────────────────────────────────────────────────────────

const RenderBlock = ({ block, index, post, analytics }) => {
  switch (block.type) {

    case "heading":
      return (
        <ContentHeading
          as={`h${Math.min(block.level || 2, 6)}`}
          $level={block.level || 2}
        >
          {block.text}
        </ContentHeading>
      );

    case "paragraph":
      return <ContentParagraph>{block.text}</ContentParagraph>;

    case "image":
      return (
        <ContentImage>
          <img
            src={`${ROOT_PATH}/${block.src}`}
            alt={block.alt || ""}
            loading="lazy"
            onLoad={() => analytics.trackImageView(block.src, block.alt, post, index)}
          />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </ContentImage>
      );

    case "video":
      return (
        <VideoWrap>
          <iframe
            src={`${YT_EMBED}/${block.id}?rel=0&modestbranding=1&enablejsapi=1`}
            title={block.caption || "Yoga video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={() => analytics.trackVideoLoad(block.id, block.caption, post, index)}
          />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </VideoWrap>
      );

    case "list":
      if (block.style === "table") {
        const [header, ...rows] = block.items;
        const headerCells = header.split("|").map((c) => c.trim());
        return (
          <TableBlock>
            <table>
              <thead>
                <tr>{headerCells.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.split("|").map((c, ci) => <td key={ci}>{c.trim()}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableBlock>
        );
      }
      return (
        <ContentList
          as={block.style === "number" ? "ol" : "ul"}
          $ordered={block.style === "number"}
        >
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ContentList>
      );

    case "quote":
      return (
        <Blockquote>
          <p>{block.text}</p>
          {block.author && <footer>— {block.author}</footer>}
        </Blockquote>
      );

    case "link":
      return (
        <ContentLink
          href={block.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`External link: ${block.text}`}
          onClick={() => analytics.trackOutboundLink(block.href, block.text, post)}
        >
          {block.text} <ExternalIcon />
        </ContentLink>
      );

    default:
      return null;
  }
};

// ─── useScrollDepth — mirrors useYogAnalytics scroll milestone pattern ─────────

const useScrollDepth = (post, analytics) => {
  const fired = useRef(new Set());

  useEffect(() => {
    if (!post) return;
    const milestones = [25, 50, 75, 100];

    const onScroll = () => {
      const pct = Math.round(
        ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
      );
      milestones.forEach((m) => {
        if (pct >= m && !fired.current.has(m)) {
          fired.current.add(m);
          analytics.trackScrollDepth(m, post);
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [post, analytics]);
};

// ─── useTimeOnPage — mirrors useYogAnalytics time milestone pattern ────────────

const useTimeOnPage = (post, analytics) => {
  useEffect(() => {
    if (!post) return;
    const start     = Date.now();
    const milestones = [30, 60, 120, 300]; // seconds — same as original

    const timers = milestones.map((s) =>
      setTimeout(() => analytics.trackTimeOnPage(s, post), s * 1000)
    );

    return () => {
      timers.forEach(clearTimeout);
      const totalSec = Math.round((Date.now() - start) / 1000);
      analytics.trackPageExit(totalSec, post);
    };
  }, [post, analytics]);
};

// ─── BlogDescription ──────────────────────────────────────────────────────────

const BlogDescription = () => {
  const { sN }     = useParams();
  const navigate   = useNavigate();
  const analytics  = useBlogAnalytics();
  const post       = Data.find((item) => String(item.sN) === String(sN));
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => { setImgFailed(false); }, [sN]);

  // ── Engagement tracking hooks ──
  useScrollDepth(post, analytics);
  useTimeOnPage(post, analytics);

  // ── Article page view on mount ──
  useEffect(() => {
    if (!post) return;
    analytics.trackArticleView(post);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.sN]);

  const handleBack = useCallback(() => {
    analytics.trackBackClick(post);
    navigate("/blog");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [navigate, analytics, post]);

  if (!post) {
    return (
      <ArticlePage>
        <EmptyState>
          <span className="icon">🔍</span>
          <h3>Post not found</h3>
          <p>This article doesn't exist or may have been removed.</p>
        </EmptyState>
      </ArticlePage>
    );
  }

  const heroImg    = imgFailed ? null : heroImgSrc(post);
  const focusTags  = (post.seo?.tags || []).slice(0, 3);

  const initials = post.author
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <ArticlePage>

      {/* ── SEO — BlogArticleMeta mirrors YogPageMeta + VideoSchemaMeta pattern ── */}
      <BlogArticleMeta post={post} heroImg={heroImg} />

      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      {/* ── Hero: image if available, otherwise designed fallback ── */}
      {heroImg ? (
        <ArticleHero>
          <img
            src={heroImg}
            alt={post.title}
            loading="eager"
            onError={() => setImgFailed(true)}
          />
          <ArticleHeroText>
            <CategoryPill style={{ marginBottom: "10px" }}>
              {post.category}
            </CategoryPill>
            <h1 style={{
              fontSize:      "clamp(1.4rem, 4vw, 2.4rem)",
              fontWeight:    900,
              color:         "#fff",
              lineHeight:    1.2,
              letterSpacing: "-0.025em",
              textShadow:    "0 2px 12px rgba(0,0,0,0.5)",
              margin:        0,
            }}>
              {post.title}
            </h1>
          </ArticleHeroText>
        </ArticleHero>
      ) : (
        <ArticleHeroPlain>
          <ArticleHeroPlainText>
            <CategoryPill>{post.category}</CategoryPill>
            <h1>{post.title}</h1>
            <div className="divider" aria-hidden="true"><span /><span /></div>
            {focusTags.length > 0 && (
              <p className="focus">
                <i aria-hidden="true" />
                Focus: {focusTags.join(", ")}
              </p>
            )}
          </ArticleHeroPlainText>
          <ArticleHeroPlainArt>
            <NoImageArt aria-hidden="true">
              <LotusArt />
            </NoImageArt>
          </ArticleHeroPlainArt>
        </ArticleHeroPlain>
      )}

      {/* ── Article body ── */}
      <ArticleInner>

        <ArticleMeta>
          <AuthorAvatar aria-hidden="true">{initials}</AuthorAvatar>
          <MetaText>
            <span className="author">{post.author}</span>
            <span className="date">{post.date}</span>
          </MetaText>
          <BackBtn type="button" onClick={handleBack} aria-label="Back to blog">
            <BackArrow /> Blog
          </BackBtn>
        </ArticleMeta>

        {post.shortDescription && (
          <ContentParagraph style={{
            fontSize:     "clamp(1rem, 2.5vw, 1.12rem)",
            fontWeight:   500,
            lineHeight:   1.75,
            marginBottom: "2rem",
            opacity:      0.85,
          }}>
            {post.shortDescription}
          </ContentParagraph>
        )}

        {/* Content blocks — analytics passed down to RenderBlock */}
        {post.content?.map((block, i) => (
          <RenderBlock
            key={i}
            block={block}
            index={i}
            post={post}
            analytics={analytics}
          />
        ))}

        {/* SEO tags — each click tracked */}
        {post.seo?.tags?.length > 0 && (
          <SeoSection>
            <SeoLabel>Tags</SeoLabel>
            <SeoTags>
              {post.seo.tags.map((tag) => (
                <SeoTag
                  key={tag}
                  onClick={() => analytics.trackTagClick(tag, post)}
                >
                  {tag}
                </SeoTag>
              ))}
            </SeoTags>
          </SeoSection>
        )}

      </ArticleInner>
    </ArticlePage>
  );
};

BlogDescription.displayName = "BlogDescription";
export default React.memo(BlogDescription);